import isEqual from 'lodash/isEqual';
import Multiselect from 'multiselect-react-dropdown';
import { useEffect, useRef, useState } from 'react';
import { formatDataFromLocalStorage, validateEmail } from '../utilities/helper';
import {
  onboardingApplication,
  updateOnboardingApplication,
  checkApplicationName,
  fetchTiers
} from '../service/apiServices';
import { useAzureData } from '../context/AzureContext';
import './OnboardingForm.css';
import { CircleChevronLeft, RotateCcw, CircleCheck, X, Plus } from 'lucide-react';
import add_app from '../assets/icon/add_app.svg';
import SearchDropDown from './SearchDropDown';
import { useNavigate } from 'react-router-dom';
import MultiSelectDropdown from './MultiSelect/MultiSelectDropdown';
import { useToast } from '../hooks/useToast';
import { CloudProvider, ToolTipFunction } from './inputField';
import useCloudProvider from '../hooks/useCloudProvider';
import { ReactComponent as I_icon } from '../assets/icon/i_icon.svg';
import PlainButton from './ButtonComponents';

import deleteIcon from '../assets/icon/deleteIcon1.svg';
type Env = {
  label: string;
  value: string;
};

type AppOption = {
  label: string;
  value: string;
};
type SPNAppIdEnvironmentMap = {
  environment: string;
  service_principal_name: string;
  application_id: string;
};
type ModelDetails = {
  model_id: string;
  model_name: string;
  prod: string;
  'non-prod': string;
  model_type: string;
  cloud_platform_id: number;
};
type OnboardingFormValue = {
  onboarding_id?: number;
  environment: string[];
  application_name: string;
  model_name: string[];
  model_id: string[];
  primary_owner: string;
  wl3_approver: string;
  application_description: string;
  secondary_owner: string;
  icc: string;
  cc: string;

  aif_rag_access: boolean;
  ai_inventory_id: string;
  onboardingstatus: string | null;
  spn_appid_env_map: SPNAppIdEnvironmentMap[] | [];
  adgroupforappaccess: string[] | [];
  tier: string;
  service_name: string;
  business_group: string;
  function_name: string;
};

const OnboardingForm = (props: any) => {
  const navigate = useNavigate();
  const availableEnvs = [
    { label: 'Non-Prod', value: 'non-prod' },
    { label: 'Prod', value: 'prod' }
  ];
  const {
    listofAppIdentities,
    formData,
    listOfModels,
    pricingDetails,
    unEditableFields,
    handleNavigation
  } = props;
  const envRef = useRef<any>(null);
  const modelRef = useRef<any>(null);
  const tierPriority = ['Standard', 'Standard (Extended Support)', 'Premium'];
  const { cloudProvidersList, selectedCloud, selectCloud } = useCloudProvider();

  const normalizeTier = (tier?: string) => {
    if (!tier) return tierPriority[0]; // default to "Standard"

    const cleaned = tier.trim().toLowerCase();
    const match = tierPriority.find((t) => t.toLowerCase() === cleaned);
    return match || tierPriority[0]; // fallback if no match
  };

  // Usage

  const [formValue, setFormValue] = useState<OnboardingFormValue>({
    ...formData,
    tier: normalizeTier(formData.tier || props.tierInfo)
  });

  useEffect(() => {
    if (!!formData?.rolesList?.length) {
      setRolesList(formData?.rolesList);
    }
  }, []);

  useEffect(() => {
    if (formData?.cloud_platform_id) {
      selectCloud(formData?.cloud_platform_id);
    }
  }, [formData?.cloud_platform_id]);

  const [initialFormData, setInitialFormData] = useState(formValue);
  const [disableSubmitButton, setDisableSubmitButton] = useState(true);
  const [selectedEnvs, setSelectedEnvs] = useState<Env[] | []>([]);
  const selectedEnvValues = selectedEnvs.map((env) => env.value);
  const [isEmailValid, setisEmailValid] = useState({
    primary_owner: true,
    wl3_approver: true,
    secondary_owner: true
  });
  const [spnValidation, setSpnValidation] = useState('');
  const [dropDownOptions, setDropDownOptions] = useState<AppOption[] | []>([]);

  const { apiToken } = useAzureData();
  const [envDropdownOpen, setEnvDropdownOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const { showSuccess } = useToast();
  const isApproved = formValue.onboardingstatus?.toLowerCase() === 'approved';
  const [filteredOptions, setFilteredOptions] = useState<ModelDetails[]>([]);
  const [tiers, setTiers] = useState<{ tierId: number; tierName: string }[]>([]);
  const [selectedTier, setSelectedTier] = useState('');

  const NameManagement = (name: any) => {
    return name?.prod?.endsWith('OSSModel')
      ? name?.model_name?.split('.')[name?.model_name?.split('.')?.length - 1]
      : name?.model_name;
  };

  const modelNameTrimmer = () => {
    return filteredOptions
      ?.map((item) => ({
        ...item,
        model_name: NameManagement(item)
      }))
      ?.filter((itm) => itm?.cloud_platform_id === selectedCloud);
  };

  useEffect(() => {
    const temp: any = [];

    if (selectedEnvs?.length === 0) {
      return;
    }
    selectedEnvs?.map((item) =>
      formValue?.spn_appid_env_map?.map((mp) => {
        if (mp?.environment === item?.value) {
          temp.push(mp);
        }
      })
    );
    setFormValue({ ...formValue, spn_appid_env_map: temp });
  }, [selectedEnvs]);

  useEffect(() => {
    if (props.tierInfo && props.tierInfo !== formValue.tier) {
      setFormValue((prev) => ({ ...prev, tier: props.tierInfo }));
    }
  }, [props.tierInfo]);

  useEffect(() => {
    setFormValue((prevState) => {
      return {
        ...prevState,
        adgroupforappaccess: [...new Set(addAccessgroupInfo(prevState))]
      };
    });
  }, [formValue.model_id, formValue.environment]);

  const defaultRoleData = { emailId: '', role: '' };
  const [rolesList, setRolesList] = useState([defaultRoleData]);
  useEffect(() => {
    const validEmails = Object.values(isEmailValid).every(Boolean);
    const isEnvSelected = formValue.environment.length > 0;
    const isModelSelected = formValue.model_name.length > 0;
    const isFormModified =
      isEnvSelected && isModelSelected && validEmails && !isEqual(formValue, initialFormData);
    setDisableSubmitButton(!isFormModified);
  }, [formValue, initialFormData, isEmailValid]);

  const [isRolesBasedButtonDisabled, setIsRolesBasedButtonDisabled] = useState(false);
  useEffect(() => {
    const temp = isEqual(initialFormData?.rolesList, rolesList);
    setIsRolesBasedButtonDisabled(temp);
  }, [rolesList]);

  useEffect(() => {
    if (formValue.environment.length > 0) {
      const preselectedValues = formValue.environment.flatMap((env) =>
        availableEnvs.filter((avEnv) => avEnv.value.toLowerCase() === env.toLowerCase())
      );
      setSelectedEnvs(preselectedValues);
    }

    //formatting the data before sending the option to <SearchDropdown> compo
    const formattedData =
      listofAppIdentities &&
      listofAppIdentities?.length > 0 &&
      formatDataFromLocalStorage(listofAppIdentities);
    setDropDownOptions(formattedData);
    //eslint-disable-next-line
  }, [listofAppIdentities?.length]);

  useEffect(() => {
    if (tempListOfModels && tempListOfModels.length > 0) {
      const grouped = tempListOfModels.map((model: ModelDetails) => ({
        ...model,
        group: model.model_type
      }));
      setFilteredOptions(grouped);
    }
  }, [listOfModels]);

  useEffect(() => {
    if (!modelRef?.current) return;
    const inputEl = document.querySelector(
      '.groupMultiSelectModel .searchBox'
    ) as HTMLInputElement | null;
    if (inputEl) {
      if (formValue.model_name.length > 0) {
        inputEl.classList.add('searchBoxOnboarding');
      } else {
        inputEl.classList.remove('searchBoxOnboarding');
      }
    }
  }, [formValue.model_name]);

  useEffect(() => {
    const getTiers = async () => {
      try {
        const response = await fetchTiers(apiToken);
        if (response?.tiers?.length) {
          const order = ['Standard', 'Standard (Extended Support)', 'Premium'];

          const sortedTiers = [...response.tiers].sort((a, b) => {
            const indexA = order.indexOf(a.tierName);
            const indexB = order.indexOf(b.tierName);

            const safeIndexA = indexA === -1 ? 999 : indexA;
            const safeIndexB = indexB === -1 ? 999 : indexB;

            return safeIndexA - safeIndexB;
          });

          setTiers(sortedTiers);
        }
      } catch (error) {
        console.error('Error fetching tiers:', error);
      }
    };

    getTiers();
  }, [apiToken]);

  const addAccessgroupInfo = (prevState: OnboardingFormValue) => {
    const selectedModels = tempListOfModels.filter((md: any) =>
      prevState.model_name?.includes(md.model_name)
    );
    const updatedAccessGroupDetails = prevState.environment.flatMap((env) =>
      selectedModels.map((model: any) => model[env.toLowerCase()])
    );
    return updatedAccessGroupDetails;
  };

  const shouldSave = (spnValue: SPNAppIdEnvironmentMap[]) => {
    let shouldSave = true;
    if (spnValue.length === 0) {
      setSpnValidation('Please add SPN name');
      shouldSave = false;
    } else if (spnValue.length === 2) {
      if (spnValue[0].service_principal_name === spnValue[1].service_principal_name) {
        setSpnValidation('SPN value for different environments cannot be same');
        shouldSave = false;
      }
    }
    const emptySpnValue = spnValue.filter((value) => value.service_principal_name.trim() === '');
    if (emptySpnValue.length > 0) {
      setSpnValidation('Please add SPN name');
      shouldSave = false;
    }
    return shouldSave;
  };

  const isFieldLocked = (fieldName: string) => {
    if (!unEditableFields) return false; // new onboarding → nothing locked
    if (isApproved) {
      // When approved, only allow these fields
      const allowed = [
        'tier',
        'environment',
        'spn',
        'model',
        'secondary_owner',
        'ai_inventory_id',
        'aif_rag_access'
      ];
      return !allowed.includes(fieldName);
    }
    // Normal edit mode (not approved) → only app name + primary owner locked
    return (
      fieldName === 'application_name' ||
      fieldName === 'primary_owner' ||
      fieldName === 'aif_rag_access'
    );
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    // Validate SPN first
    if (!shouldSave(formValue.spn_appid_env_map)) return;

    // ✅ Perform Application Name Validation only in Create mode
    if (!unEditableFields) {
      if (!formValue.application_name) {
        setSpnValidation('Application name is required');
        return;
      }

      try {
        const appNameResult = await checkApplicationName(formValue.application_name, apiToken);
        const exists = appNameResult?.exists ?? false;

        if (exists) {
          setSpnValidation('Application name already exists');
          return; // stop submission
        } else {
          setSpnValidation('');
        }
      } catch (err) {
        console.error('Error checking application name:', err);
        setSpnValidation('Error validating application name');
        return;
      }
    }

    setDisableSubmitButton(true);
    // console.log('Payload on Apply click:', formValue);
    const formValue_ = { ...formValue, cloud_platform_id: selectedCloud, rolesList: rolesList };

    // console.log('Payload on Apply click:', formValue_);

    let response;
    // console.log(formValueTemp, 'vvvformValueTemp');
    // ✅ Edit Mode → Update existing onboarding
    if (unEditableFields) {
      // const onboardingId = formValueTemp.onboarding_id;
      const onboardingId = formValue.onboarding_id;
      if (!onboardingId) {
        console.error('No onboarding_id found in formValue');
        setDisableSubmitButton(false);
        return;
      }

      response = await updateOnboardingApplication(onboardingId, formValue_, apiToken);
      response && setSuccess(true);
    }
    // ✅ Create Mode → Create new onboarding
    else {
      response = await onboardingApplication(formValue_, apiToken);
      response && setSuccess(true);
    }

    // console.log('Response from backend:', response);

    // resetForm();
  };

  useEffect(() => {
    if (success) {
      showSuccess('Applied successfully!', { toastId: 'app-save-toast' });
      setSuccess(false);
      navigate('/myapps');
    }
  }, [success, navigate, showSuccess]);

  useEffect(() => {
    if (success) {
      showSuccess('Applied successfully!', { toastId: 'app-save-toast' });
      setSuccess(false);
      navigate('/myapps');
    }
  }, [success, navigate, showSuccess]);

  const resetForm = () => {
    setFormValue({
      environment: [],
      application_name: '',
      model_name: [],
      model_id: [],
      primary_owner: '',
      wl3_approver: '',
      application_description: '',
      secondary_owner: '',
      icc: '',
      cc: '',
      aif_rag_access: false,
      ai_inventory_id: '',
      onboardingstatus: null,
      spn_appid_env_map: [],
      adgroupforappaccess: [],
      tier: '',
      service_name: '',
      business_group: '',
      function_name: ''
    });
    setSelectedEnvs([]);
    if (envRef.current) envRef.current.resetSelectedValues();
    if (modelRef.current) modelRef.current.resetSelectedValues();
    setDisableSubmitButton(true);
  };

  type AppIdentity = {
    spn_name: string;
    application_id: string;
    [key: string]: any;
  };

  const handleEnvInfo = (value: string, env: string) => {
    setSpnValidation('');
    setFormValue((prevState) => {
      const normalizedEnv = env.toLowerCase();

      const existingIndex = prevState.spn_appid_env_map.findIndex(
        (data) => data.environment.toLowerCase() === normalizedEnv
      );

      // Find the application_id safely
      const selectedAppIdentity = value
        ? listofAppIdentities.find((appIdentity: AppIdentity) => appIdentity.spn_name === value)
            ?.application_id || ''
        : '';

      let updatedCredentials = [...prevState.spn_appid_env_map];

      if (existingIndex !== -1) {
        // Update the existing entry
        updatedCredentials[existingIndex] = {
          environment: env, // preserve casing from dropdown
          service_principal_name: value || '',
          application_id: selectedAppIdentity
        };
      } else {
        // Add new entry if not found
        updatedCredentials.push({
          environment: env,
          service_principal_name: value || '',
          application_id: selectedAppIdentity
        });
      }

      return {
        ...prevState,
        spn_appid_env_map: updatedCredentials
      };
    });
  };

  //Handling Environment selection from dropdown
  const handleEnvSelection = async (envs: Env[]) => {
    setSelectedEnvs([...envs]);
    const environments = envs.map((env) => env.value);
    setFormValue((prev) => ({ ...prev, environment: [...environments] }));
  };

  //Handling Environment removal from dropdown
  const handleEnvRemoval = async (envs: Env[]) => {
    setSelectedEnvs([...envs]);

    const environments = envs.map((env) => env.value);
    setFormValue((prev) => ({ ...prev, environment: [...environments] }));

    setFormValue((prevState) => {
      const updatedCredentials = prevState.spn_appid_env_map.filter((data) =>
        environments.includes(data.environment)
      );
      return {
        ...prevState,
        spn_appid_env_map: updatedCredentials
      };
    });
  };

  //Handling of Model Selection
  const handleModelsSelection = async (models: ModelDetails[]) => {
    const modelNames = models.map((model) => model.model_name);
    const modelIds = models.map((model) => model.model_id);
    setFormValue((prevState) => ({
      ...prevState,
      model_name: [...modelNames],
      model_id: [...modelIds]
    }));
  };

  //Handling of Model Removal
  const handleModelsRemoval = async (models: ModelDetails[]) => {
    const modelNames = models.map((model) => model.model_name);
    const modelIds = models.map((model) => model.model_id);
    setFormValue((prevState) => ({
      ...prevState,
      model_name: [...modelNames],
      model_id: [...modelIds]
    }));
  };

  // handle changes in primary_owner, secondary_owner and wl3+ approver
  const handleValueChange = (e: any, label: string) => {
    setFormValue((prevValue) => ({
      ...prevValue,
      [label]: e.target.value.trim()
    }));
    setisEmailValid((prevState) => ({ ...prevState, [label]: true }));
    if (e.target.value !== '') {
      if (!validateEmail(e.target.value)) {
        setisEmailValid((prevState) => ({ ...prevState, [label]: false }));
      }
    }
  };

  const renderEnvMultiSelector = () => {
    const selectedValues = formValue.environment.flatMap((t) =>
      availableEnvs.filter((av) => av.value.toLowerCase() === t.toLowerCase())
    );

    return (
      <Multiselect
        options={availableEnvs}
        placeholder="Select Environment"
        selectedValues={selectedValues}
        showCheckbox={true}
        showArrow={true}
        onSelect={handleEnvSelection}
        onRemove={handleEnvRemoval}
        displayValue="label"
        hidePlaceholder={true}
        ref={envRef}
        id="env"
      />
    );
  };

  const handleModelSearch = (searchText: string) => {
    if (!searchText) {
      // reset
      const grouped = tempListOfModels.map((model: ModelDetails) => ({
        ...model,
        group: model.model_type
      }));
      setFilteredOptions(grouped);
      return;
    }

    const query = searchText.toLowerCase();
    const filtered = tempListOfModels
      .filter(
        (m: ModelDetails) =>
          m.model_name.toLowerCase().includes(query) || m.model_type.toLowerCase().includes(query) // 👈 search by type too
      )
      .map((model: ModelDetails) => ({
        ...model,
        group: model.model_type
      }));

    setFilteredOptions(filtered);
  };

  // for managing the view of Model Names in the FE Model DropDown
  const tempListOfModels = listOfModels?.map((item: any) => ({
    ...item,
    model_name: NameManagement(item)
  }));

  const renderModelSelector = () => {
    const selectedValues1 = tempListOfModels.filter((model: any) =>
      formValue?.model_name?.includes(model.model_name)
    );

    const selectedValues2 = listOfModels.filter((model: any) =>
      formValue?.model_name?.includes(model.model_name)
    );

    const selectedValues3 = selectedValues2?.map((item: any) => ({
      ...item,
      model_name: NameManagement(item)
    }));
    // selectedValues1 and selectedValues3 , selectedValues3 is used to manage APPs created with older ModelNames
    const selectedValues = selectedValues1?.length ? selectedValues1 : selectedValues3;

    // normalize data so that we always have a "group" key
    const groupedOptions = tempListOfModels.map((model: ModelDetails) => ({
      ...model,
      group: model.model_type // ✅ group by model_type
    }));

    // split selected into visible (first 2) + hidden (rest)
    const visible = selectedValues.slice(0, 2);
    const hidden = selectedValues.slice(2);

    // handler to clear hidden selections
    const clearHidden = () => {
      handleModelsRemoval(visible);
    };
    return (
      <div className="position-relative groupMultiSelectModel_container">
        <Multiselect
          options={modelNameTrimmer()}
          placeholder="Select Model"
          selectedValues={selectedValues}
          showCheckbox
          showArrow
          customArrow
          displayValue="model_name"
          groupBy="group"
          ref={modelRef}
          id="model"
          onSelect={handleModelsSelection}
          onRemove={handleModelsRemoval}
          onSearch={handleModelSearch}
          className="groupMultiSelectModel"
          hidePlaceholder
          style={{ chips: { display: 'none' } }}
          customCloseIcon={<X className="ms-1" size={12} />}
        />
        {selectedValues.length > 0 && (
          <div className="overlay-selected">
            <div className="d-flex flex-wrap align-items-center gap-1">
              {selectedValues.slice(0, 2).map((item: any, index: any) => (
                <ToolTipFunction placement="bottom" toolTipText={item.model_name}>
                  <span key={index} className="chip">
                    {item.model_name}
                    <X
                      className="ms-1 chip-remove"
                      size={12}
                      onClick={() => {
                        const newSelected = selectedValues.filter((_: any, i: any) => i !== index);
                        handleModelsRemoval(newSelected);
                      }}
                    />
                  </span>
                </ToolTipFunction>
              ))}

              {selectedValues.length > 2 && (
                <span
                  className="chip chip-more"
                  onClick={clearHidden}
                  title="Remove excess selections">
                  <Plus size={10} className="me-1" />
                  {selectedValues.length - 2} more
                  <X className="ms-1 chip-remove" size={12} />
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const [createdEnv, setCreatedEnv] = useState([]);
  const availableOptionsFunction = () => {
    if (unEditableFields) {
      if (createdEnv?.length === 1) {
        return availableEnvs;
      } else if (createdEnv?.length > 1) {
        const temp = availableEnvs?.filter(
          (item) => item?.value === formValue?.spn_appid_env_map?.[0]?.environment
        );
        return temp;
      }
    }
    return availableEnvs;
  };

  useEffect(() => {
    // to run this code in editable mode only
    if (unEditableFields) {
      const AppsData = JSON.parse(sessionStorage.getItem('AppsData') || '');
      const filteredData = AppsData?.filter(
        (app: any) => app?.application_name === formValue.application_name
      );
      const tempEnvData = filteredData?.map((item: any) => item?.environment);
      setCreatedEnv(tempEnvData);
    }
  }, []);

  // useEffect(() => {
  //   if (containerRef.current) {
  //     containerRef.current.scrollTop = containerRef.current.scrollHeight;
  //   }
  // }, [rolesList]);

  const emailVerification = (val: any): boolean => {
    if (!val) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !emailRegex.test(val);
  };

  const newRoleButtonFunction = () => {
    if (
      rolesList?.length > 9 ||
      rolesList?.some((role) => role?.emailId == '' || role?.role == '')
    ) {
      return <></>;
    }

    return (
      <PlainButton buttonAction={() => setRolesList([defaultRoleData, ...rolesList])}>
        + New Role ({10 - rolesList?.length})
      </PlainButton>
    );
  };

  const handleUserRole = (value: any, key: string, index: number) => {
    const temp = [...rolesList];
    temp[index] = { ...temp[index], [key]: value };
    setRolesList(temp);
  };

  const containerRef = useRef(null);

  return (
    <div className="row pt-1 pb-2 mb-3 text-left">
      <form className="form-container" onSubmit={handleSubmit}>
        <div className="p-1">
          <CloudProvider
            cloudProvidersList={cloudProvidersList}
            selectedCloud={selectedCloud}
            selectCloud={selectCloud}
            disabled={unEditableFields}
            sideAction={() => {
              // this function is for resetting hese values on change of Cloud Provider
              setFormValue((prevState) => ({
                ...prevState,
                model_name: [],
                model_id: [],
                spn_appid_env_map: [],
                environment: []
              }));
              setSelectedEnvs([]);
            }}
          />
          {/* Row 5: CMDB Service Name, Business Group, Function */}
          <div className="row mb-3">
            {/* CMDB Service Name */}
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                CMDB Service Name<span style={{ color: 'red' }}>*</span>
              </p>
              <input
                type="text"
                className={`form-control ${isFieldLocked('service_name') ? 'non-editable' : ''}`}
                placeholder="CMDB Service Name"
                name="service_name"
                value={formValue.service_name || ''}
                required
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    service_name: e.target.value.trim()
                  }))
                }
                readOnly={isFieldLocked('service_name')}
              />
              <small
                style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#555' }}>
                Please provide the service name as defined in the{' '}
                <a
                  href="https://cmdbviewer.unilever.com/cmdb/search"
                  target="_blank"
                  rel="noopener noreferrer">
                  CMDB service catalog
                </a>
                .
              </small>
            </div>

            {/* Business Group */}
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                Business Group<span style={{ color: 'red' }}>*</span>
              </p>
              <select
                className={`form-select ${isFieldLocked('business_group') ? 'non-editable' : ''}`}
                value={formValue.business_group}
                required
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    business_group: e.target.value
                  }))
                }
                disabled={isFieldLocked('business_group')}>
                <option value="">Select Business Group</option>
                <option value="Beauty and Wellbeing">Beauty and Wellbeing</option>
                <option value="Home Care">Home Care</option>
                <option value="Nutrition">Nutrition</option>
                <option value="Personal Care">Personal Care</option>
                <option value="Others">Others</option>
              </select>
            </div>

            {/* Function */}
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                Function<span style={{ color: 'red' }}>*</span>
              </p>
              <select
                className={`form-select ${isFieldLocked('function_name') ? 'non-editable' : ''}`}
                value={formValue.function_name}
                required
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    function_name: e.target.value
                  }))
                }
                disabled={isFieldLocked('function_name')}>
                <option value="">Select Function</option>
                <option value="Business Operations">Business Operations</option>
                <option value="Communication & Corporate Affairs">
                  Communication & Corporate Affairs
                </option>
                <option value="Corporate Audit">Corporate Audit</option>
                <option value="Digital & Commercial">Digital & Commercial</option>
                <option value="Finance">Finance</option>
                <option value="Human Resources">Human Resources</option>
                <option value="Legal">Legal</option>
                <option value="Research & Development">Research & Development</option>
                <option value="Sustainability">Sustainability</option>
                <option value="Others">Others</option>
              </select>
            </div>
          </div>

          <div className="row mb-3">
            {/* Application Name */}
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                Application Name<span style={{ color: 'red' }}>*</span>
              </p>
              <input
                type="text"
                className={`${unEditableFields ? 'form-control non-editable' : 'form-control'}`}
                placeholder="Application Name"
                value={formValue.application_name}
                name="app_name"
                required
                readOnly={unEditableFields}
                onChange={(e) => {
                  setFormValue((prev) => ({
                    ...prev,
                    application_name: e.target.value
                  }));
                  setSpnValidation(''); // clear error while typing
                }}
                onBlur={async (e) => {
                  if (unEditableFields) return;
                  const appName = e.target.value.trim();
                  if (!appName) return;

                  try {
                    const response = await checkApplicationName(appName, apiToken);

                    // Directly read exists from response
                    const exists = response?.exists ?? false;

                    if (exists) {
                      setSpnValidation('Application name already exists');
                    } else {
                      setSpnValidation('');
                    }
                  } catch (err) {
                    console.error('Error checking application name:', err);
                    setSpnValidation('Error validating application name');
                  }
                }}
              />
              {spnValidation && <p style={{ color: 'red', marginTop: '5px' }}>{spnValidation}</p>}
            </div>

            {/* Tier */}
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                Tier<span style={{ color: 'red' }}>*</span>
              </p>
              <select
                value={formValue?.tier || ''}
                className="form-select"
                required
                onChange={(e) =>
                  setFormValue((prevState) => ({
                    ...prevState,
                    tier: e.target.value
                  }))
                }>
                {tiers.map((tier) => (
                  <option key={tier.tierId} value={tier.tierName}>
                    {tier.tierName}
                  </option>
                ))}
              </select>
            </div>

            {/* Environment */}
            <div className="col-md-4">
              <MultiSelectDropdown
                label="Environment"
                required={true}
                options={availableOptionsFunction()}
                selectedValues={selectedEnvValues}
                onChange={(values: string[]) => {
                  // Update selectedEnvs state
                  const updatedEnvs = availableOptionsFunction()?.filter((env) =>
                    values.includes(env.value)
                  );
                  setSelectedEnvs(updatedEnvs);

                  // Update formValue.environment
                  setFormValue((prev) => ({ ...prev, environment: values }));
                }}
                placeholder="Select Environment"
                maxDisplayItems={2}
              />
            </div>

            {/* SPN boxes (each one gets its own column) */}
            {selectedEnvs.length > 0 &&
              selectedEnvs.map((env) => {
                // Find the SPN for this environment
                const selectedSPN =
                  formValue?.spn_appid_env_map?.find(
                    (item) => item.environment.toLowerCase() === env.value.toLowerCase()
                  )?.service_principal_name || '';

                return (
                  <div key={env.value} className="col-md-4">
                    <p className="my-1 unl-label-p">
                      {selectedCloud === 1
                        ? `Service Principal Name for ${env.value} (SPN)`
                        : ` Service Account Name for ${env.value} (SAN)`}

                      <span style={{ color: 'red' }}>*</span>
                    </p>
                    <SearchDropDown
                      options={selectedCloud === 1 ? dropDownOptions : []}
                      selectedVal={selectedSPN}
                      placeholder="Search SPN..."
                      handleChange={(val: string) => handleEnvInfo(val, env.value)}
                    />
                  </div>
                );
              })}
          </div>

          {/* Row 2: Application Description */}
          <div className="row mb-3">
            <div className="col-12">
              <p className="my-1 unl-label-p">
                Application Description<span style={{ color: 'red' }}>*</span>
              </p>
              <textarea
                className={`form-control app-desc-textarea ${
                  isFieldLocked('application_description') ? 'non-editable' : ''
                }`}
                placeholder="Application Description"
                name="desc"
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    application_description: e.target.value
                  }))
                }
                value={formValue.application_description}
                required
                readOnly={isFieldLocked('application_description')}
              />
            </div>
          </div>

          {/* Row 3: Primary Owner, Secondary Owner, WL3+ Approver */}
          <div className="row mb-3">
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                Primary Owner<span style={{ color: 'red' }}>*</span>
              </p>
              <input
                type="text"
                onChange={(e) => handleValueChange(e, 'primary_owner')}
                className={`${unEditableFields ? 'form-control non-editable' : 'form-control'}`}
                placeholder="Primary Owner"
                name="primary_owner"
                value={formValue.primary_owner}
                required
                readOnly={unEditableFields}
              />
              {!isEmailValid.primary_owner && <p style={{ color: 'red' }}>Invalid email address</p>}
            </div>

            <div className="col-md-4">
              <p className="my-1 unl-label-p">Secondary Owner</p>
              <input
                type="text"
                className={`form-control ${isFieldLocked('secondary_owner') ? 'non-editable' : ''}`}
                placeholder="Secondary Owner"
                name="secondary_owner"
                onChange={(e) => handleValueChange(e, 'secondary_owner')}
                value={formValue.secondary_owner}
                readOnly={isFieldLocked('secondary_owner')}
              />
              {!isEmailValid.secondary_owner && (
                <p style={{ color: 'red' }}>Invalid email address</p>
              )}
            </div>

            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                WL3+ Approver<span style={{ color: 'red' }}>*</span>
              </p>
              <input
                type="text"
                className={`form-control ${isFieldLocked('wl3_approver') ? 'non-editable' : ''}`}
                placeholder="WL3+ Approver"
                name="wl3_approver"
                value={formValue.wl3_approver}
                required
                onChange={(e) => handleValueChange(e, 'wl3_approver')}
                readOnly={isFieldLocked('wl3_approver')}
              />
              {!isEmailValid.wl3_approver && <p style={{ color: 'red' }}>Invalid email address</p>}
            </div>
          </div>

          <div
            style={{
              maxHeight: '250px',
              borderRadius: '5px',
              border: '1px solid #CFD4D9 ',
              padding: '5px',
              // backgroundColor: 'Red',
              overflow: 'auto'
            }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{ height: '20%' }}>
              <p className="my-1 unl-label-p">Add User</p>

              {newRoleButtonFunction()}
            </div>
            <div
              ref={containerRef}
              style={{
                overflowY: 'auto',
                scrollbarWidth: 'none',
                height: '80%'
              }}>
              {rolesList.map((item, index) => (
                <div className="row mb-3 ">
                  <div className="col-md-4">
                    <p className="my-1 unl-label-p">
                      Role Type
                      {/* <span style={{ color: 'red' }}>*</span> */}
                    </p>
                    <select
                      className={`form-select`}
                      value={item?.role}
                      // required
                      onChange={(e) => handleUserRole(e.target.value, 'role', index)}>
                      <option value={''}>Select Role</option>
                      <option value={'rag_playground_user'}>RAG Playground User</option>
                      <option value={'app_user'}>App User</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <p className="my-1 unl-label-p">
                      User ({index + 1}) Email ID
                      {/* <span style={{ color: 'red' }}>*</span> */}
                    </p>
                    <div>
                      <div className="d-flex ">
                        <input
                          type="text"
                          className={`form-control`}
                          disabled={rolesList[index]?.role == ''}
                          placeholder="User Email Id"
                          name="User Email Id"
                          onChange={(e) => {
                            handleUserRole(e.target.value, 'emailId', index);
                          }}
                          value={item?.emailId}
                        />

                        {
                          <button
                            type="button"
                            onClick={() =>
                              setRolesList((prev) => prev.filter((_, i) => i !== index))
                            }
                            className="btn btn-sm btnDelete my-auto"
                            style={{ maxWidth: '40px', height: '100%' }}>
                            <img src={deleteIcon} alt="delete" />
                          </button>
                        }
                      </div>
                      {emailVerification(item?.emailId) && (
                        <p style={{ color: 'red' }}>Invalid Email Address</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Row 4: ICC, CC, AI Inventory ID */}
          <div className="row mb-3">
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                ICC<span style={{ color: 'red' }}>*</span>
              </p>
              <input
                type="text"
                className={`form-control ${isFieldLocked('icc') ? 'non-editable' : ''}`}
                placeholder="ICC"
                name="icc"
                value={formValue.icc}
                required
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    icc: e.target.value.trim()
                  }))
                }
                readOnly={isFieldLocked('icc')}
              />
            </div>

            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                CC<span style={{ color: 'red' }}>*</span>
              </p>
              <input
                type="text"
                className={`form-control ${isFieldLocked('cc') ? 'non-editable' : ''}`}
                placeholder="CC"
                name="cc"
                value={formValue.cc}
                required
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    cc: e.target.value.trim()
                  }))
                }
                readOnly={isFieldLocked('cc')}
              />
            </div>

            <div className="col-md-4">
              <p className="my-1 unl-label-p">AI Inventory ID</p>
              <input
                type="text"
                className={`form-control ${isFieldLocked('ai_inventory_id') ? 'non-editable' : ''}`}
                placeholder="AI Inventory ID"
                name="ai_inventory_id"
                value={formValue.ai_inventory_id}
                onChange={(e) =>
                  setFormValue((prevValue) => ({
                    ...prevValue,
                    ai_inventory_id: e.target.value.trim()
                  }))
                }
                readOnly={isFieldLocked('ai_inventory_id')}
              />
            </div>
          </div>

          {/* Row 5: Model Name, RAG Builder Subscription, Empty */}
          <div className="row mb-3">
            {/* Model Name */}
            {/* */}
            <div className="col-md-4">
              <p className="my-1 unl-label-p">
                Model<span style={{ color: 'red' }}>*</span>
                <ToolTipFunction
                  placement="top"
                  toolTipText={'Available options update when you change "Cloud Provider"'}>
                  <I_icon />{' '}
                </ToolTipFunction>
              </p>

              <div className="p-0">{tempListOfModels && renderModelSelector()}</div>
            </div>
            {/* */}

            <div className="col-md-4">
              <p className="my-1 unl-label-p">RAG Builder Subscription</p>
              <div className="mt-3">
                <input
                  id="RAG"
                  type="checkbox"
                  onChange={() =>
                    setFormValue((prevValue) => ({
                      ...prevValue,
                      aif_rag_access: !prevValue.aif_rag_access
                    }))
                  }
                  checked={formValue.aif_rag_access}
                  disabled={isFieldLocked('aif_rag_access')}
                />
                <label className="ps-2 mb-1" htmlFor="RAG">
                  Subscribe to AI Foundation RAG Builder
                </label>
              </div>
            </div>
            {/* Empty column for alignment */}
            <div className="col-md-4"></div>
          </div>
        </div>

        {/* Buttons */}
        <div className="px-1 pt-1 button-container">
          {!unEditableFields && (
            <button type="reset" className="btn reset-btn" onClick={resetForm}>
              <RotateCcw strokeWidth={2} size={30} />
              Reset
            </button>
          )}
          <button
            type="submit"
            className="btn apply-btn"
            disabled={disableSubmitButton && isRolesBasedButtonDisabled}>
            <CircleCheck size={30} strokeWidth={2} />
            Apply
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingForm;
